import { JSX, useLayoutEffect, useState } from 'react';
import { KibChipAction } from '@chewy/kib-chips-react';
import { KibModal } from '@chewy/kib-overlays-react';

import { BackIcon } from '@src/components/icons/back';
import { ChewyCarePlusLogo } from '@src/components/icons/chewy-heath-careplus';
import { PhoneIcon } from '@src/components/icons/phone';
import { useAnalytics } from '@src/hooks/use-analytics.hook';
import { getUserAgent } from '@src/helpers/user-agent.helper';
import { chewyCarePlusSupport } from '@src/constants/phone-numbers';
import { useRouteUtils } from '@src/hooks/use-route-utils.hook';

import '@chewy/kib-overlays-styles/dist/styles/kib-modal.css';
import '@chewy/kib-chips-styles/dist/styles/kib-chip-action.css';
import '@src/components/header/index.scss';

export const Header = ({
  onBackClicked,
  hideBackButton = false,
  hideCallBtn = false,
}:
  | {
      onBackClicked: () => void;
      hideBackButton?: false;
      hideCallBtn?: boolean;
    }
  | {
      onBackClicked?: never;
      hideBackButton: true;
      hideCallBtn?: boolean;
    }): JSX.Element => {
  const { trackClick } = useAnalytics();
  const { isNma } = getUserAgent();
  const { getInternalPath } = useRouteUtils();

  const [state, setState] = useState<{
    showHelpTooltip: boolean;
  }>({
    showHelpTooltip: false,
  });

  const { showHelpTooltip } = state;

  useLayoutEffect((): any => {
    const containerNode = document.querySelector(
      '.hc-pet-insurance__container',
    );

    return showHelpTooltip
      ? containerNode.setAttribute('aria-hidden', 'true')
      : containerNode.removeAttribute('aria-hidden');
  }, [showHelpTooltip]);

  return (
    <header id="hc-pet-insurance__header">
      <div className="container">
        {!isNma && !hideBackButton && (
          <>
            <span id="hc-pet-insurance__back-btn--large">
              <KibChipAction
                action={onBackClicked}
                icon={<BackIcon />}
                variant="transparent"
              >
                Back
              </KibChipAction>
            </span>
            <span id="hc-pet-insurance__back-btn--small">
              <KibChipAction
                action={onBackClicked}
                icon={<BackIcon />}
                variant="transparent"
              ></KibChipAction>
            </span>
          </>
        )}

        <a
          id="hc-pet-insurance__header-logo"
          href={getInternalPath('/pet-insurance')}
          title="Go to Landing Page"
        >
          <ChewyCarePlusLogo />
        </a>

        {/* Call Help with tooltip modal */}
        {!hideCallBtn && (
          <>
            <span id="hc-pet-insurance__call-btn--large">
              <KibChipAction
                icon={<PhoneIcon />}
                action={() =>
                  setState((prev) => ({
                    ...prev,
                    showHelpTooltip: true,
                  }))
                }
              >
                24/7 Help
              </KibChipAction>
            </span>
            <span id="hc-pet-insurance__call-btn--small">
              <KibChipAction
                variant="transparent"
                action={() =>
                  trackClick({
                    eventLabel: 'call-us',
                    extraData: {
                      page_type: 'pet-insurance',
                    },
                  })
                }
                href={chewyCarePlusSupport.link}
              >
                Call Us
              </KibChipAction>
            </span>
            <KibModal
              title=""
              open={showHelpTooltip}
              onClose={() =>
                setState((prev) => ({
                  ...prev,
                  showHelpTooltip: false,
                }))
              }
              id="kib-help-tooltip"
            >
              <div className="hcpi-help-tooltip">
                <p className="hcpi-help-tooltip__body">
                  Get help from our experts 24/7
                </p>
                <p className="hcpi-help-tooltip__phone-number">
                  <a
                    href={chewyCarePlusSupport.link}
                    title={`Call ${chewyCarePlusSupport.label}`}
                    onClick={() =>
                      trackClick({
                        eventLabel: 'call-us',
                        extraData: {
                          page_type: 'pet-insurance',
                        },
                      })
                    }
                  >
                    {chewyCarePlusSupport.phoneNumber}
                  </a>
                </p>
                <p className="hcpi-help-tooltip__faq-link">
                  <a
                    href={getInternalPath('/pet-insurance/#pi-careplus__faq')}
                    title="Go to frequently asked questions"
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    Our FAQ&apos;s
                  </a>
                </p>
              </div>
            </KibModal>
          </>
        )}
      </div>
    </header>
  );
};
